import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnAlarm, SnAuditOperation, SnCamera, SnContentType } from '@lib/api/entities';
import { SnAuditService, SnIncidentsAPI } from '@lib/api/services';
import { PursuitService } from '@lib/cameras/services/pursuit.service';
import {
  ChannelsToShareDialogComponent,
  ChatMediaMessageDescriptionDialogComponent,
} from '@lib/communication/twiliochat/components';
import { SnChannel } from '@lib/communication/twiliochat/models';
import { SnMessagesService } from '@lib/communication/twiliochat/services';
import { SnVideoPlayerComponent } from '@lib/player/video-player';
import { t } from '@lib/translation';
import { SentryVaultComponent } from '@lib/vault';
import { SnAbilityService } from 'libs/ability/src/lib/services';
import { SnapAiImageDialogComponent } from 'libs/sentry/src/lib/components/snap-ai-image-dialog/snap-ai-image-dialog.component';

@Component({
  selector: 'sn-share-image-dialog',
  templateUrl: './share-image-dialog.component.html',
  styleUrls: ['./share-image-dialog.component.scss'],
})
export class SnShareImageDialogComponent implements OnInit, OnDestroy {
  public croppedImg: HTMLCanvasElement;
  public isSending: boolean;
  public isAdding: boolean;
  public isMedia: boolean;
  public isPursuit: boolean = false;

  public readonly canClip: boolean;

  public readonly canSendToVault: boolean;
  private readonly _destroy$ = new Subject();

  get hideSnapAi(): boolean {
    return !!this.data.hideSnapAi;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      url: string;
      camera?: SnCamera;
      alarm?: SnAlarm;
      playerRef?: SnVideoPlayerComponent;
      hideSnapAi?: boolean;
    },
    private readonly _matDialog: MatDialog,
    private readonly _messages: SnMessagesService,
    private readonly _dialogRef: MatDialogRef<SnShareImageDialogComponent>,
    private readonly _auditService: SnAuditService,
    private readonly _incidentsAPI: SnIncidentsAPI,
    private readonly _toastrService: ToastrService,
    private readonly _ability: SnAbilityService,
    private readonly _pursuitService: PursuitService,
  ) {
    this.canClip = this._ability.can({
      subject: 'cameras/camera',
      action: 'clip_recordings',
    });
    this.isPursuit = this._pursuitService.getPursuitValue();
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public cropped(img: HTMLCanvasElement): void {
    this.croppedImg = img;
  }

  public download(): void {
    const payload: any = {
      object: {
        id: this.data.alarm?.id ? this.data.alarm?.id : this.data.camera?.id,
        name: this.data.alarm?.type ? this.data.alarm?.type : this.data.camera?.name,
        contentType: this.data.alarm ? SnContentType.Alarm : SnContentType.Camera,
      },
      operation: SnAuditOperation.DownloadScreenShot,
      changeData: {
        downloadScreenShotTime: this.data.alarm ? new Date() : this.data.playerRef.timeStamp,
      },
    };
    this._auditService.createAudit([payload]).pipe(takeUntil(this._destroy$)).subscribe();
    const now = new Date();
    const name = `${now.getTime()}`;
    const downloadLink = document.createElement('a');
    downloadLink.setAttribute('download', `snapshot-${name}.png`);
    this.croppedImg.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      downloadLink.setAttribute('href', url);
      downloadLink.click();
    });
  }

  public snapAi(): void {
    this.croppedImg.toBlob((blob) => {
      const now = new Date();
      const name = `${now.getTime()}`;
      const file: any = blob;
      file.lastModifiedDate = new Date();
      file.name = `snapshot-${name}.jpeg`;
      if (this.isPursuit) {
        const dataSnap = {
          file,
          camera: this.data.camera,
        };
        this._pursuitService.setSnapAi(dataSnap);
        this._dialogRef.close();
      } else {
        this._matDialog.open(SnapAiImageDialogComponent, {
          data: {
            file,
            camera: this.data.camera,
          },
        });
      }
    });
  }

  public sendToVault(url: string): void {
    this.croppedImg.toBlob((blob) => {
      const now = new Date();
      const name = `${now.getTime()}`;
      const file: any = blob;
      file.lastModifiedDate = new Date();
      file.name = `snapshot-${name}.jpeg`;
      const formData = new FormData();
      formData.append('files', file, file.name);
      const dialog = this._matDialog.open(SentryVaultComponent);
      dialog
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((vaultId) => {
          const { id } = vaultId;
          if (id) {
            this._addVaultImage(id, formData);
          }
        });
    });
  }

  public shareToChat(): void {
    const dialog = this._matDialog.open(ChannelsToShareDialogComponent);
    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((channel) => {
        if (channel.id) {
          this._send(channel);
        }
      });
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  private _addVaultImage(vault: string, formData: any): void {
    this.isAdding = true;
    this._toastrService.overlayContainer = this.data.playerRef?.toastContainer;
    this._incidentsAPI
      .sendRequestImage(vault, formData)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.isAdding = false;
          this._dialogRef.close();
        },
        error: (error) => {
          console.error(error);
        },
      });
  }

  private _send(channel: SnChannel): void {
    this._matDialog
      .open(ChatMediaMessageDescriptionDialogComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        this._sendMediaMessage(channel, result);
      });
  }

  private _sendMediaMessage(channel: SnChannel, data: any): void {
    this.isSending = true;
    this.croppedImg.toBlob((blob) => {
      const now = new Date();
      const name = `${now.getTime()}`;
      const file: any = blob;
      file.lastModifiedDate = new Date();
      file.name = `snapshot-${name}.jpeg`;
      this._toastrService.overlayContainer = this.data.playerRef?.toastContainer;
      this._messages
        .sendMediaMessage(channel, file, data?.description)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: () => {
            this.isSending = false;
            this._dialogRef.close();
          },
          error: (error) => {
            this.isSending = false;
            this._toastrService.error(t('vault.incidents.service.unableToSendImage'));
            console.error(error);
          },
        });
    });
  }
}
